/** 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Dashboard welcomes the user in its content; the shell title belongs to other pages.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ComponentType, type CSSProperties, type ReactNode } from 'react';
import { findPageTitleItem, type PageTitleItem } from './page-title.state';

interface PageTitleContextValue {
  items: readonly PageTitleItem[];
  setItems: (items: readonly PageTitleItem[]) => void;
  explicitTitle: { pathname: string; title: string; owner: symbol } | null;
  registerTitle: (pathname: string, title: string) => () => void;
}

const PageTitleContext = createContext<PageTitleContextValue | null>(null);
export const usePageTitleContext = () => useContext(PageTitleContext);
const EmbeddedPageTitleContext = createContext<string | null>(null);
export const useEmbeddedPageTitle = () => useContext(EmbeddedPageTitleContext);
/** Embedded editors keep the enclosing workspace title in the platform shell. */
export function EmbeddedPageTitle({ title, children }: { title: string; children: ReactNode }) {
  return <EmbeddedPageTitleContext.Provider value={title}>{children}</EmbeddedPageTitleContext.Provider>;
}

/** One provider per mounted shell; nested outlets and embedded pages keep their own scope. */
export function withPageTitles<Props extends object>(Layout: ComponentType<Props>) {
  function LayoutWithPageTitles(props: Props) {
    const [items, setItems] = useState<readonly PageTitleItem[]>([]);
    const [explicitTitle, setExplicitTitle] = useState<PageTitleContextValue['explicitTitle']>(null);
    const registerTitle = useCallback((pathname: string, title: string) => {
      const owner = Symbol('page-title');
      setExplicitTitle({ pathname, title, owner });
      return () => setExplicitTitle(current => current?.owner === owner ? null : current);
    }, []);
    const value = useMemo(() => ({ items, setItems, explicitTitle, registerTitle }), [items, explicitTitle, registerTitle]);
    return <PageTitleContext.Provider value={value}><Layout {...props} /></PageTitleContext.Provider>;
  }
  LayoutWithPageTitles.displayName = `WithPageTitles(${Layout.displayName || Layout.name || 'Layout'})`;
  return LayoutWithPageTitles;
}

export function shouldShowPageTitle(pathname: string, dashboardPath: string): boolean {
  const normalize = (path: string) => path.split(/[?#]/, 1)[0].replace(/\/+$/, '') || '/';
  const current = normalize(pathname);
  return current !== '/' && current !== normalize(dashboardPath) && !/\/dashboard$/i.test(current);
}

export function PageTitle({ pathname, dashboardPath, children, className = 'page-title', style }: {
  pathname: string; dashboardPath: string; children: ReactNode; className?: string; style?: CSSProperties;
}) {
  const context = usePageTitleContext();
  const explicit = context?.explicitTitle?.pathname === pathname ? context.explicitTitle.title.trim() : '';
  const items = context?.items ?? [];
  const legacyTitle = typeof children === 'string' ? children.trim() : children;
  const title = explicit || findPageTitleItem(pathname, items, true) || legacyTitle || findPageTitleItem(pathname, items);
  return shouldShowPageTitle(pathname, dashboardPath) ? <h1 className={className} style={style}>{title}</h1> : null;
}
